import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Modal,
  List,
  ListItem,
  ListItemButton,
  Divider,
  Tooltip,
} from "@mui/material";
import { blue } from "@mui/material/colors";
import AddLocationIcon from "@mui/icons-material/AddLocation";
import DeleteIcon from "@mui/icons-material/Delete";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import axios from "axios";
import markerRdvIcon from "../../icon/marker-rdv.svg";
import RdvAttendance from "./RdvAttendance";
import RdvAttendanceQRModal from "./RdvAttendanceQRModal";

export default function RdvList(props) {
  const [dsRdvList, setDsRdvList] = useState([]);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [allRdvList, setAllRdvList] = useState([]);
  const [adding, setAdding] = useState(false);
  const [attendanceRdv, setAttendanceRdv] = useState(null);
  const [qrRdv, setQrRdv] = useState(null);
  const [attendanceCounts, setAttendanceCounts] = useState({});
  const dsid = (props.dsid || "").toString().trim();
  const dsnm = (props.dsnm || "").toString().trim();

  const fetchAttendanceCounts = useCallback(async () => {
    if (!dsid || !props.apiAddr || dsRdvList.length === 0) {
      setAttendanceCounts({});
      return;
    }
    const next = {};
    await Promise.all(
      dsRdvList.map(async (row) => {
        const rdvId = row.rdv_id ?? row.id ?? row.snum;
        if (rdvId == null) return;
        try {
          const res = await fetch(
            `${props.apiAddr}/get/rdvattendance?dsid=${encodeURIComponent(dsid)}&rdv_id=${rdvId}`,
          );
          const list = await res.json();
          const arr = Array.isArray(list) ? list : [];
          next[row.snum] = arr.filter((r) => !r.check_out_dt).length;
        } catch (e) {
          next[row.snum] = 0;
        }
      }),
    );
    setAttendanceCounts((prev) => ({ ...prev, ...next }));
  }, [dsid, props.apiAddr, dsRdvList]);

  useEffect(() => {
    fetchAttendanceCounts();
  }, [fetchAttendanceCounts]);

  const fetchDsRdvList = async () => {
    if (!props.apiAddr || !dsid) return;
    try {
      const res = await axios.get(
        `${props.apiAddr}/get/getdsrdvs?dsid=${encodeURIComponent(dsid)}`,
      );
      const data = Array.isArray(res.data) ? res.data : [];
      setDsRdvList(data);
    } catch (e) {
      console.warn("getdsrdvs fetch failed", e);
      setDsRdvList([]);
    }
  };

  const fetchAllRdvList = async () => {
    if (!props.apiAddr) return;
    try {
      const res = await axios.get(`${props.apiAddr}/get/getrdvlist`);
      const raw = res.data;
      const data = Array.isArray(raw)
        ? raw
        : raw && typeof raw === "object"
          ? Object.values(raw)
          : [];
      const hq =
        (typeof localStorage !== "undefined" && localStorage.hq?.trim()) || "";
      const filtered = data.filter((item) => (item.hq || "").trim() === hq);
      setAllRdvList(filtered);
    } catch (e) {
      console.warn("getrdvlist fetch failed", e);
      setAllRdvList([]);
    }
  };

  useEffect(() => {
    if (dsid) fetchDsRdvList();
    else setDsRdvList([]);
  }, [props.apiAddr, dsid]);

  useEffect(() => {
    const onRefresh = () => {
      fetchDsRdvList();
      if (addModalOpen) fetchAllRdvList();
    };
    window.addEventListener("rdvListRefresh", onRefresh);
    return () => window.removeEventListener("rdvListRefresh", onRefresh);
  }, [props.apiAddr, dsid, addModalOpen]);

  useEffect(() => {
    const onAttendanceRefresh = (e) => {
      const eventDsid = (e?.detail?.dsid || "").toString().trim();
      if (!eventDsid || eventDsid === dsid) fetchAttendanceCounts();
    };
    window.addEventListener("rdvAttendanceRefresh", onAttendanceRefresh);
    return () => window.removeEventListener("rdvAttendanceRefresh", onAttendanceRefresh);
  }, [dsid, fetchAttendanceCounts]);

  /** 검은색 채움 원문자: 1~10 ❶…❿(U+2776), 11~20 ⓫…⓴(U+24EB), 그 이상 (n) */
  const getCircledNum = (n) => {
    if (n >= 1 && n <= 10) return String.fromCodePoint(0x2776 + n - 1);
    if (n >= 11 && n <= 20) return String.fromCodePoint(0x24eb + n - 11);
    return `(${n})`;
  };

  const handleRowClick = (row) => {
    const lat = (row.lat || "").toString().trim();
    const lon = (row.lon || "").toString().trim();
    if (lat && lon) {
      sessionStorage.ondslat = lat;
      sessionStorage.ondslon = lon;
      sessionStorage.onsiteid = "";
      sessionStorage.centerOnRdv = "1";
      sessionStorage.centerOnRdvTitle = (row.rname || "집결지").toString();
      if (props.mapfresh) props.mapfresh();
    }
  };

  const handleAddFromList = async (rdv) => {
    if (!props.apiAddr || !dsid || adding) return;
    setAdding(true);
    try {
      const res = await axios.post(`${props.apiAddr}/post/adddsrdv`, {
        dsid,
        rdv_id: rdv.id,
        rname: (rdv.rname || "").trim(),
        lat: (rdv.lat || "").toString().trim(),
        lon: (rdv.lon || "").toString().trim(),
      });
      if (res.data?.result === "success") {
        fetchDsRdvList();
        setAddModalOpen(false);
        if (props.mapfresh) props.mapfresh();
      } else {
        alert(res.data?.message || "추가에 실패했습니다.");
      }
    } catch (e) {
      console.warn("adddsrdv failed", e);
      const msg =
        e.response?.data?.message ||
        "이미 추가된 집결지이거나 추가에 실패했습니다.";
      alert(msg);
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (e, rdv) => {
    e.stopPropagation();
    if (!window.confirm(`"${rdv.rname}"을(를) 이 재난에서 제거하시겠습니까?`))
      return;
    if (
      !window.confirm("정말 삭제하시겠습니까? 삭제 후에는 복구할 수 없습니다.")
    )
      return;
    try {
      const res = await axios.get(
        `${props.apiAddr}/get/deldsrdv?snum=${rdv.snum}`,
      );
      if (res.data?.result === "success") {
        fetchDsRdvList();
        if (props.mapfresh) props.mapfresh();
      } else {
        alert("삭제에 실패했습니다.");
      }
    } catch (e) {
      console.warn("deldsrdv failed", e);
      alert("삭제에 실패했습니다.");
    }
  };

  const handleOpenAddModal = () => {
    fetchAllRdvList();
    setAddModalOpen(true);
  };

  const handleAddNewRdv = () => {
    setAddModalOpen(false);
    if (typeof sessionStorage !== "undefined") sessionStorage.modalSort = "RDV";
    if (props.onShowRDV) props.onShowRDV();
  };

  if (!dsid) return null;

  const alreadyAddedIds = new Set(
    dsRdvList.map((r) => r.rdv_id).filter((id) => id != null),
  );

  return (
    <Box
      sx={{
        mt: 1,
        p: 1,
        backgroundColor: blue[50],
        borderRadius: 1,
        border: "1px solid",
        borderColor: blue[200],
        ...(props.sx || {}),
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 0.5,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <Box
            component="img"
            src={markerRdvIcon}
            alt=""
            sx={{ width: 20, height: 20, display: "block" }}
          />
          자원집결지, 지원인력 관리
        </Typography>
        <Button
          size="small"
          startIcon={<AddLocationIcon />}
          onClick={handleOpenAddModal}
          sx={{ fontSize: "0.75rem", minWidth: 0, py: 0.25 }}
        >
          집결지 추가
        </Button>
      </Box>

      {dsRdvList.length === 0 ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", py: 1.5 }}
        >
          해당 재난에 적용된 자원집결지가 없습니다.
        </Typography>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 0.5,
            maxHeight: 160,
            overflowY: "auto",
          }}
        >
          {dsRdvList.map((row, index) => {
            const coordText =
              row.lat && row.lon ? `${row.lat}, ${row.lon}` : "(좌표 없음)";
            const fullName = row.rname || "(이름 없음)";
            const maxNameLen = 14;
            const displayName =
              fullName.length > maxNameLen
                ? fullName.slice(0, maxNameLen) + "..."
                : fullName;
            const startNum = (props.startOrderNumber ?? 0) + 1;
            const buttonLabel = `${getCircledNum(startNum + index)} ${displayName}`;
            return (
              <Box
                key={row.snum}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  border: "1px solid",
                  borderColor: blue[300],
                  borderRadius: 1,
                  overflow: "hidden",
                  "&:hover": { borderColor: blue[500] },
                }}
              >
                <Tooltip
                  title={row.rname ? `${row.rname}\n${coordText}` : coordText}
                  placement="top"
                  enterDelay={300}
                >
                  <Button
                    size="small"
                    fullWidth
                    onClick={() => handleRowClick(row)}
                    sx={{
                      justifyContent: "flex-start",
                      textAlign: "left",
                      textTransform: "none",
                      fontSize: "0.8rem",
                      py: 0.5,
                      minHeight: 32,
                      minWidth: 0,
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      bgcolor: "transparent",
                      "&:hover": { bgcolor: blue[100] },
                    }}
                  >
                    {buttonLabel}
                  </Button>
                </Tooltip>
                {/* 출퇴근수 | QR버튼 | 삭제버튼 */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "stretch",
                    gap: 0,
                    borderTop: "1px solid",
                    borderColor: blue[200],
                  }}
                >
                  <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", minWidth: 0, py: 0.25 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: blue[800] }}>
                      {attendanceCounts[row.snum] ?? "—"}
                    </Typography>
                  </Box>
                  <Tooltip title="출퇴근 관리" placement="top">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setQrRdv(row);
                      }}
                      sx={{
                        flex: 1,
                        borderRadius: 0,
                        "&:hover": { bgcolor: blue[100] },
                      }}
                    >
                      <QrCode2Icon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                  </Tooltip>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => handleDelete(e, row)}
                    sx={{
                      flex: 1,
                      borderRadius: 0,
                      "&:hover": { bgcolor: blue[100] },
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: "1rem" }} />
                  </IconButton>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      <Modal open={addModalOpen} onClose={() => setAddModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: 400,
            bgcolor: "background.paper",
            borderRadius: 1,
            boxShadow: 24,
            p: 2,
          }}
        >
          <Typography variant="h6" sx={{ mb: 1.5, fontWeight: "bold" }}>
            자원집결지 추가
          </Typography>
          <Divider sx={{ mb: 1.5 }} />
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ mb: 0.5 }}
          >
            기본관리리스트에서 선택
          </Typography>
          <List
            dense
            sx={{
              maxHeight: 200,
              overflowY: "auto",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              mb: 1.5,
            }}
          >
            {allRdvList.length === 0 ? (
              <ListItem>
                <Typography variant="caption" color="text.secondary">
                  등록된 집결지가 없습니다.
                </Typography>
              </ListItem>
            ) : (
              allRdvList.map((rdv) => (
                <ListItem key={rdv.id} disablePadding>
                  <ListItemButton
                    onClick={() => handleAddFromList(rdv)}
                    disabled={alreadyAddedIds.has(rdv.id) || adding}
                  >
                    <Typography variant="body2">
                      {rdv.rname || "(이름 없음)"}
                      {alreadyAddedIds.has(rdv.id) && (
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 0.5 }}
                        >
                          (이미 추가됨)
                        </Typography>
                      )}
                    </Typography>
                  </ListItemButton>
                </ListItem>
              ))
            )}
          </List>
          <Divider sx={{ my: 1 }} />
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ mb: 0.5 }}
          >
            새로운 집결지 추가
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddLocationIcon />}
            onClick={handleAddNewRdv}
            fullWidth
          >
            자원집결지관리에서 신규 추가
          </Button>
          <Box sx={{ mt: 1.5, display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={() => setAddModalOpen(false)}>닫기</Button>
          </Box>
        </Box>
      </Modal>

      <RdvAttendanceQRModal
        open={qrRdv != null}
        onClose={() => {
          setQrRdv(null);
          fetchAttendanceCounts();
        }}
        dsid={dsid}
        rdvId={qrRdv?.rdv_id}
        rname={qrRdv?.rname}
        dsnm={dsnm}
        apiAddr={props.apiAddr}
        onOpenAttendance={() => {
          if (qrRdv) setAttendanceRdv(qrRdv);
          setQrRdv(null);
        }}
      />
      <RdvAttendance
        open={attendanceRdv != null}
        onClose={() => setAttendanceRdv(null)}
        dsid={dsid}
        rdvId={attendanceRdv?.rdv_id}
        rname={attendanceRdv?.rname}
        apiAddr={props.apiAddr}
      />
    </Box>
  );
}
